import { supabase } from "@/lib/supabase";
import { useCentralStore } from "../../store/Central";
import { useCallback, useEffect, useState } from "react";
import { JobStatusEnum } from "@/lib/types/job";
import { JobsTable } from "../../components/job/jobs-table.component";
import { Checkbox, FormControlLabel } from "@mui/material";
import { SelectionRange } from "@progress/kendo-react-dateinputs";
import { CustomDateRangePicker } from "../../components/custom-date-range-picker/custom-date-range-picker.component";
import { ExtendedJobDocument } from "../../lib/utils/calculate";
import { JobDocumentTypeEnum } from "../job-page/job-document/job-document.types";

export const JobsArchivePage = () => {
	const { clientId, organizationId } = useCentralStore((state) => ({
		clientId: state.clientId,
		organizationId: state.organization?.id ?? "",
	}));

	const [allDocuments, setAllDocuments] = useState<ExtendedJobDocument[]>([]);
	const [selectedDocTypes, setSelectedDocTypes] = useState<JobDocumentTypeEnum[]>([
		JobDocumentTypeEnum.DeliveryNote,
		JobDocumentTypeEnum.CreditNote,
		JobDocumentTypeEnum.Quotation,
	]);
	const [dateValue, setDateValue] = useState<SelectionRange>({
		start: null,
		end: null,
	});
	const [filteredDocuments, setFilteredDocuments] = useState<ExtendedJobDocument[]>([]);

	const fetchDocuments = useCallback(async () => {
		if (!clientId || !organizationId) return;

		let query = supabase
			.from("job_documents_extended_view")
			.select("*")
			.eq("organization_id", organizationId)
			.eq("job_client_id", clientId)
			.eq("job_document_status", JobStatusEnum.ARCHIVED)
			.neq("job_document_type", JobDocumentTypeEnum.Tracing);

		if (dateValue.start && dateValue.end) {
			query = query
				.gte("job_document_date", dateValue.start.toISOString())
				.lte("job_document_date", dateValue.end.toISOString());
		}

		query = query.order("job_document_date", { ascending: false });

		const { data } = await query;

		setAllDocuments((data as ExtendedJobDocument[]) ?? []);
	}, [clientId, organizationId, dateValue]);

	useEffect(() => {
		fetchDocuments();
	}, [fetchDocuments]);

	useEffect(() => {
		const filtered = allDocuments.filter((doc) =>
			selectedDocTypes.includes(doc.job_document_type as JobDocumentTypeEnum)
		);
		setFilteredDocuments(filtered);
	}, [selectedDocTypes, allDocuments]);

	const toggleDocType = (docType: JobDocumentTypeEnum) => {
		setSelectedDocTypes((prev) =>
			prev.includes(docType) ? prev.filter((t) => t !== docType) : [...prev, docType]
		);
	};

	return (
		<div className="p-4 h-full flex flex-col gap-2">
			<div className="flex items-center gap-4 flex-wrap">
				<CustomDateRangePicker dateVal={dateValue} handleDateChange={setDateValue} />
				<FormControlLabel
					control={
						<Checkbox
							checked={selectedDocTypes.includes(JobDocumentTypeEnum.DeliveryNote)}
							onChange={() => toggleDocType(JobDocumentTypeEnum.DeliveryNote)}
							disableRipple
						/>
					}
					label="Lieferschein"
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={selectedDocTypes.includes(JobDocumentTypeEnum.CreditNote)}
							onChange={() => toggleDocType(JobDocumentTypeEnum.CreditNote)}
							disableRipple
						/>
					}
					label="Gutschrift"
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={selectedDocTypes.includes(JobDocumentTypeEnum.Quotation)}
							onChange={() => toggleDocType(JobDocumentTypeEnum.Quotation)}
							disableRipple
						/>
					}
					label="Kostenvoranschlag"
				/>
			</div>
			<JobsTable documents={filteredDocuments} />
		</div>
	);
};
